//@flow
import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import {usePage} from '../../../page/utils';
import {Grid, Card, Hidden} from '@material-ui/core';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import Skeleton from '@material-ui/lab/Skeleton';
import SnackBar from '../../../../elements/SnackBar/SnackBar';
import {useHistory, Switch, Route} from 'react-router-dom';
import {useFeatureSpec, useIsFeatureInTier} from '../../../../utils/component';

import {colors} from '../../../../assets/styles/colors';

import TierRestrictionModal from '../../../../elements/TierRestrictionModal/TierRestrictionModal';
import PromotionCard from './PromotionCard/PromotionCard';
import PromotionContainer from '../containers/PromotionContainer';
import PromotionSuccessContainer from '../containers/PromotionSuccessContainer';
import SortPromotionModal from './SortPromotionModal/SortPromotionModal';
import LoadingSpinner from '../../../../elements/LoadingSpinner/LoadingSpinner';

import {useIsMinimalPlan} from '../../../store/utils';
import {useMinimalTierContext} from '../../../../context/MinimalProvider';
import type {Currency, Store, SinglePromotion} from '../../../../entities';
import PromotionsDisabledModal from './PromotionsDisabledModal/PromotionsDisabledModal';

type Props = {|
  isSaving?: boolean,
  promotions?: SinglePromotion[],
  isFetchingPromotions?: boolean,
  fetchPromotions: () => void,
  cleanPromotion: () => void,
  updatePromotion: () => void,
  toggleFilter: (state?: any) => void,
  sortPromotion: ({sortPromotionRequest: {[key: string]: any}}) => void,
  selectedState?: string,
  error?: string,
  currency?: Currency,
  store: Store,
  updatePromotionSuccess?: boolean,
|};

const useStyles = makeStyles(theme => ({
  root: {
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    padding: '.25rem',
    [theme.breakpoints.down('sm')]: {
      background: 'transparent',
    },
    [theme.breakpoints.up('md')]: {
      padding: '.75rem',
    },
    [theme.breakpoints.up('lg')]: {
      padding: '1rem',
    },
  },
  filterWrapper: {
    [theme.breakpoints.down('sm')]: {
      background: '#FFFFFF',
      padding: '16px',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      borderRadius: '12px',
    },
  },
  bodyContent: {
    [theme.breakpoints.down('sm')]: {
      background: '#FFFFFF',
      padding: '16px',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      borderRadius: '12px',
    },
  },
  filterButton: {
    color: colors.grey600,
    fontWeight: 'normal',
    borderRadius: '8px',
    padding: '4px 16px',
    marginRight: theme.spacing(1),
  },
  filterButtonActive: {
    color: colors.purple3,
    backgroundColor: colors.lightPurple,
    fontWeight: 600,
  },
  skeleton: {
    borderRadius: '8px',
  },
  whiteButton: {
    backgroundColor: 'white',
    color: colors.purple3,
    fontWeight: 600,
    padding: '4px 13px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    '&:hover': {
      backgroundColor: colors.grey200,
    },
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(2),
    },
    marginLeft: 4,
    marginBottom: 12,
  },
  flexFilterWrap: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  sortBtn: {
    border: '1px solid #E8E8ED',
    color: colors.grey700,
    padding: theme.spacing(0, 2),
  },
  sortBtnMobile: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row-reverse',
    marginBottom: theme.spacing(2),
  },
}));

export default usePage<Props>(Promotions, 'Promotions');

function Promotions(props: Props) {
  const {
    isFetchingPromotions,
    isSaving,
    promotions,
    toggleFilter,
    selectedState,
    cleanPromotion,
    fetchPromotions,
    updatePromotion,
    error,
    currency,
    store,
    sortPromotion,
    updatePromotionSuccess,
  } = props;
  const classes = useStyles();
  const history = useHistory();

  const isMinimal = useIsMinimalPlan();

  const {
    openRestriction,
    handleCloseRestriction,
    handleOpenRestriction,
  } = useMinimalTierContext();

  const hasPromotionFeature = useIsFeatureInTier('STORE_PROMOTION');

  const featureSpec = useFeatureSpec('STORE_PROMOTION');

  const [openModalProTier, setOpenModalProTier] = useState(false);
  const [openSortModal, setOpenSortModal] = useState(false);

  const shouldRestrictedTier = !isMinimal && !hasPromotionFeature;

  const handleEnabledOption = (state?: string) => {
    toggleFilter(state);
  };

  useEffect(() => {
    if (hasPromotionFeature) {
      fetchPromotions();
    }
  }, [fetchPromotions, hasPromotionFeature, selectedState]);

  const handleCreatePromotion = () => {
    if (!!store && !hasPromotionFeature) {
      if (isMinimal) handleOpenRestriction();
      else setOpenModalProTier(true);
    } else {
      history.push(`/marketing/promotions/new`);
    }
  };

  const shouldCreatePromotions = useMemo(() => {
    if (promotions) {
      if (featureSpec && featureSpec.limit) {
        return (
          promotions.filter(c => c.enabled).length < Number(featureSpec.limit)
        );
      }
      return true;
    } else return false;
  }, [featureSpec, promotions]);

  useEffect(() => {
    handleOpenRestriction();
  }, [handleOpenRestriction]);

  const filterWrapper = (
    <>
      {!shouldRestrictedTier && (
        <div className={classes.flexFilterWrap}>
          <div className={classes.filterWrapper}>
            <CustomButton
              className={`${classes.filterButton} ${
                selectedState === '0' ? classes.filterButtonActive : ''
              }`}
              onClick={() => handleEnabledOption('0')}
            >
              Activos
            </CustomButton>
            <CustomButton
              className={`${classes.filterButton} ${
                selectedState === '1' ? classes.filterButtonActive : ''
              }`}
              onClick={() => handleEnabledOption('1')}
            >
              Ver Todos
            </CustomButton>
          </div>
          <Hidden smDown>
            <CustomButton
              disabled={!hasPromotionFeature}
              className={classes.sortBtn}
              onClick={() => setOpenSortModal(true)}
            >
              Ordenar
            </CustomButton>
          </Hidden>
        </div>
      )}
    </>
  );

  return (
    <Switch>
      <Route exact path="/marketing/promotions">
        <PromotionsDisabledModal
          open={openRestriction}
          handleClose={handleCloseRestriction}
        />
        {isSaving && <LoadingSpinner />}
        <TierRestrictionModal
          open={openModalProTier}
          handleClose={() => setOpenModalProTier(false)}
        />
        <SortPromotionModal
          open={openSortModal}
          handleClose={() => setOpenSortModal(false)}
          promotionsArr={promotions}
          sortPromotion={sortPromotion}
          isSaving={isSaving}
        />
        {updatePromotionSuccess && (
          <SnackBar
            mode="success"
            className={classes.snackBarError}
            message="Promoción actualizada correctamente"
          />
        )}
        {hasPromotionFeature && (
          <SnackBar
            mode="error"
            className={classes.snackBarError}
            message={error}
          />
        )}
        <Card className={classes.root}>
          <Grid container justify="space-between" direccion="row">
            {filterWrapper}
            {!!store && shouldRestrictedTier && (
              <div>
                <CustomButton className={classes.whiteButton} to="/tiers-list">
                  Mejorar a TiendaDa PRO
                </CustomButton>
              </div>
            )}
          </Grid>

          <div className={classes.bodyContent}>
            <Hidden mdUp>
              <div className={classes.sortBtnMobile}>
                <CustomButton
                  className={classes.sortBtn}
                  onClick={() => setOpenSortModal(true)}
                >
                  Ordenar
                </CustomButton>
              </div>
            </Hidden>
            <Grid
              container
              direction="row"
              justify="flex-start"
              alignItems="stretch"
              spacing={3}
              style={{
                opacity: isMinimal ? 0.5 : undefined,
                pointerEvents: isMinimal ? 'none' : undefined,
              }}
            >
              {hasPromotionFeature && (
                <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                  <PromotionCard
                    type="CREATE"
                    cleanPromotion={cleanPromotion}
                    handleClick={handleCreatePromotion}
                  />
                </Grid>
              )}
              {isFetchingPromotions
                ? Array.from({length: 1}, (x, i) => (
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={3} key={i}>
                      <Skeleton
                        className={classes.skeleton}
                        variant="rect"
                        height={220}
                      />
                    </Grid>
                  ))
                : promotions && promotions.length > 0
                ? promotions.map(
                    (promotion: SinglePromotion, index: number) => (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={6}
                        lg={4}
                        xl={3}
                        key={`promotion-${index}`}
                      >
                        <PromotionCard
                          store={store}
                          promotion={promotion}
                          currency={currency}
                          cleanPromotion={cleanPromotion}
                          updatePromotion={updatePromotion}
                          shouldCreatePromotions={shouldCreatePromotions}
                        />
                      </Grid>
                    )
                  )
                : null}
            </Grid>
          </div>
        </Card>
      </Route>
      <Route
        exact
        path="/marketing/promotions/:promotionId"
        component={PromotionContainer}
      />
      <Route
        exact
        path="/marketing/promotions/creation/success"
        component={PromotionSuccessContainer}
      />
    </Switch>
  );
}

Promotions.propTypes = {
  promotions: PropTypes.array,
  isSaving: PropTypes.bool,
  isFetchingPromotions: PropTypes.bool,
  fetchPromotions: PropTypes.func.isRequired,
  error: PropTypes.string,
  currency: PropTypes.object,
  store: PropTypes.object.isRequired,
  selectedState: PropTypes.string,
  updatePromotionSuccess: PropTypes.bool,
  toggleFilter: PropTypes.func.isRequired,
  sortPromotion: PropTypes.func.isRequired,
  updatePromotion: PropTypes.func.isRequired,
  cleanPromotion: PropTypes.func.isRequired,
};
